<div class="summary-step">
  <!-- Success State -->
  @if (importSuccess) {
    <div class="success-state">
      <div class="success-illustration">
        <div class="success-checkmark">
          <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle class="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
            <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
          </svg>
        </div>
      </div>

      <div class="success-content">
        <div class="success-badge">
          <i class="material-symbols-rounded">check_circle</i>
          Import Successful
        </div>
        
        <h2 class="success-title">All set! Your bills are imported.</h2>
        
        <p class="success-description">
          I've successfully processed and imported <strong>{{ importResponse?.summary?.billCount || totalBills }}</strong>
          bill{{ (importResponse?.summary?.billCount || totalBills) === 1 ? '' : 's' }} into your portfolio.
          @if (replacedText) {
            {{ replacedText }}.
          }
        </p>

        <div class="success-actions">
          <button class="btn-success-secondary" (click)="goToPortfolio()">
            <i class="material-symbols-rounded">arrow_back</i>
            {{ goBackLabel }}
          </button>
          @if (showViewCustomer) {
            <button class="btn-success-primary" (click)="goToCustomer()">
              <i class="material-symbols-rounded">visibility</i>
              View Customer
            </button>
          }
        </div>
      </div>
    </div>
  } @else {
    <app-parent-context-banner></app-parent-context-banner>

    <!-- Stats Bar -->
    <div class="stats-bar">
      @if (showLenStat) {
        <div class="stat-item">
          <span class="stat-value">
            {{ summaryData.length }}
          </span>
          <span class="stat-label">Legal {{ summaryData.length === 1 ? 'Entity' : 'Entities' }}</span>
        </div>
        <div class="stat-divider"></div>
      }
      @if (showSaStat) {
        <div class="stat-item">
          <span class="stat-value">
            {{ newEntities.sas + existingEntities.sas }}
          </span>
          <span class="stat-label">{{ (newEntities.sas + existingEntities.sas) === 1 ? 'Address' : 'Addresses' }}</span>
        </div>
        <div class="stat-divider"></div>
      }
      @if (showMeterStat) {
        <div class="stat-item">
          <span class="stat-value">
            {{ newEntities.meters + existingEntities.meters }}
          </span>
          <span class="stat-label">{{ (newEntities.meters + existingEntities.meters) === 1 ? 'Meter' : 'Meters' }}</span>
        </div>
        <div class="stat-divider"></div>
      }
      <div class="stat-item">
        <span class="stat-value">{{ totalBills }}</span>
        <span class="stat-label">{{ totalBills === 1 ? 'Bill' : 'Bills' }}</span>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item highlight">
        <span class="stat-value">{{ totalAmount | currency:'USD':'symbol':'1.2-2' }}</span>
        <span class="stat-label">Total Amount</span>
      </div>
    </div>

    <!-- Bills matching an existing bill: merged, replaced or skipped -->
    @if (mergingCount > 0 || replacingCount > 0 || skippedCount > 0) {
      <div class="duplicate-summary">
        @if (mergingCount > 0) {
          <div class="duplicate-stat merging">
            <i class="material-symbols-rounded">merge</i>
            <span class="duplicate-stat-label">Merging into existing bills</span>
            <span class="duplicate-stat-count">{{ mergingCount }}</span>
          </div>
        }
        @if (replacingCount > 0) {
          <div class="duplicate-stat replacing">
            <i class="material-symbols-rounded">swap_horiz</i>
            <span class="duplicate-stat-label">Replacing existing bills</span>
            <span class="duplicate-stat-count">{{ replacingCount }}</span>
          </div>
        }
        @if (skippedCount > 0) {
          <div class="duplicate-stat skipped">
            <i class="material-symbols-rounded">skip_next</i>
            <span class="duplicate-stat-label">Skipped, already imported</span>
            <span class="duplicate-stat-count">{{ skippedCount }}</span>
          </div>
        }
        <ul class="duplicate-list">
          @for (row of duplicateRows; track row.billGroupId) {
            <li>
              <span>{{ row.text }} ·</span>
              <button type="button" class="duplicate-link" (click)="reviewDuplicate(row)">{{ row.actionLabel }}</button>
            </li>
          }
        </ul>
      </div>
    }

    <!-- Fast Track Banner (collapsed by default) -->
    @if (state.fastTrackMode() === 'fast' && fastTrackSkippedSteps.length > 0) {
      <div class="fast-track-banner" [class.collapsed]="fastTrackBannerCollapsed">
        <div class="ft-banner-header" (click)="fastTrackBannerCollapsed = !fastTrackBannerCollapsed">
          <i class="material-symbols-rounded ft-bolt">bolt</i>
          <span class="ft-banner-title">
            Express: {{ fastTrackSkippedSteps.length }} step{{ fastTrackSkippedSteps.length !== 1 ? 's' : '' }} auto-completed
          </span>
          <i class="material-symbols-rounded ft-expand">{{ fastTrackBannerCollapsed ? 'expand_more' : 'expand_less' }}</i>
        </div>
        @if (!fastTrackBannerCollapsed) {
          <div class="ft-banner-body">
            @for (step of fastTrackSkippedSteps; track step.key) {
              <div class="ft-step-item">
                <i class="material-symbols-rounded">bolt</i>
                <span>{{ step.label }}</span>
                @if (step.reason) {
                  <span class="ft-step-reason">— {{ step.reason }}</span>
                }
              </div>
            }
            <button class="ft-switch-btn" (click)="switchToManualReview()">
              <i class="material-symbols-rounded">manage_search</i>
              Switch to Manual Review
            </button>
          </div>
        }
      </div>
    }

    <!-- Entity Tree -->
    <div class="entity-tree">
      @for (len of summaryData; track len.groupId) {
        <div class="len-group">
          @if (showLenInTree) {
            <div class="entity-row len-row">
              <i class="material-symbols-rounded entity-icon">account_balance</i>
              <div class="entity-name-col">
                <span class="entity-type-label len-type-label">Legal Entity</span>
                <span class="entity-name">{{ len.name }}</span>
              </div>
              @if (len.isNew) {
                <span class="entity-badge new">
                  <i class="material-symbols-rounded">check</i>
                  New
                </span>
              } @else {
                <span class="entity-badge existing">EXISTING</span>
              }
            </div>
          }

          @for (sa of len.serviceAddresses; track sa.groupId) {
            <div class="sa-group">
              @if (showSaInTree) {
                <div class="entity-row sa-row">
                  <i class="material-symbols-rounded entity-icon">home_work</i>
                  <div class="entity-name-col">
                    <span class="entity-type-label sa-type-label">Service Address</span>
                    <span class="entity-name">
                      @if (sa.name) {<span class="sa-name">{{ sa.name }}</span> · }{{ sa.address }}
                    </span>
                  </div>
                  @if (!len.isNew) {
                    @if (sa.isNew) {
                      <span class="entity-badge new">
                        <i class="material-symbols-rounded">check</i>
                        New
                      </span>
                    } @else {
                      <span class="entity-badge existing">EXISTING</span>
                    }
                  }
                </div>
              }

              @for (meter of sa.meters; track meter.groupId) {
                <div class="meter-group">
                  <div class="entity-row meter-row clickable" (click)="toggleMeter(meter)">
                    <i class="material-symbols-rounded entity-icon">speed</i>
                    <div class="entity-name-col">
                      <span class="entity-type-label meter-type-label">Meter</span>
                      <span class="entity-name">{{ meter.uan }}</span>
                    </div>
                    @if (!sa.isNew) {
                      @if (meter.isNew) {
                        <span class="entity-badge new">
                          <i class="material-symbols-rounded">check</i>
                          New
                        </span>
                      } @else {
                        <span class="entity-badge existing">EXISTING</span>
                      }
                    }
                    <span class="meter-bill-count">
                      <i class="material-symbols-rounded">receipt_long</i>
                      {{ meter.billDocCount }} {{ meter.billDocCount === 1 ? 'bill' : 'bills' }}
                    </span>
                    @if (meter.energyPlanLabel) {
                      <span class="entity-badge energy-plan">
                        <i class="material-symbols-rounded">electric_bolt</i>
                        {{ meter.energyPlanLabel }}
                      </span>
                    }
                    <span class="meter-total">{{ meter.totalAmount | currency:'USD':'symbol':'1.2-2' }}</span>
                    <i class="material-symbols-rounded expand-icon">{{ meter.expanded ? 'expand_less' : 'expand_more' }}</i>
                  </div>
                  @if (meter.expanded) {
                    <div class="bills-table">
                      @for (bill of meter.bills; track bill.groupId) {
                        <div class="bill-row clickable" [class.active]="activeBillGroupId === bill.groupId && !activeChildFileId" (click)="previewBill(bill)">
                          @if (bill.children) {
                            <button class="pair-expand-btn"
                                    [title]="bill.expanded ? 'Hide source bills' : 'Show source bills'"
                                    (click)="toggleBillChildren(bill); $event.stopPropagation()">
                              <i class="material-symbols-rounded">{{ bill.expanded ? 'remove' : 'add' }}</i>
                            </button>
                          } @else {
                            <span class="pair-expand-spacer"></span>
                          }
                          <i class="material-symbols-rounded bill-icon">receipt_long</i>
                          <span class="bill-dates">
                            {{ bill.startDate }} – {{ bill.endDate }}
                          </span>
                          @if (bill.isDual) {
                            <span class="dual-badge">DUAL</span>
                          }
                          <span class="bill-provider">{{ bill.provider }}</span>
                          <span class="bill-amount">{{ bill.amount | currency:'USD':'symbol':'1.2-2' }}</span>
                        </div>
                        @if (bill.expanded && bill.children) {
                          @for (child of bill.children; track child.fileId) {
                            <div class="bill-child-row clickable" [class.active]="activeChildFileId === child.fileId" (click)="previewChild(bill, child)">
                              <i class="material-symbols-rounded bill-icon">description</i>
                              <span class="child-kind">{{ child.kind }}</span>
                              <span class="bill-dates">{{ child.label }}</span>
                            </div>
                          }
                        }
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Fast Track Warnings -->
    @if (fastTrackWarningsList.length > 0) {
      <div class="fast-track-warnings">
        @for (warning of fastTrackWarningsList; track warning) {
          <div class="ft-warning-item">
            <i class="material-symbols-rounded">info</i>
            <span>{{ warning }}</span>
          </div>
        }
      </div>
    }

    <!-- Validation Warnings (cross-LEN duplicates and other free-text errors) -->
    @if (validationErrors.length > 0) {
      <div class="validation-warnings">
        <div class="validation-header">
          <i class="material-symbols-rounded">warning</i>
          <span>Please fix the following issues before importing:</span>
        </div>
        @for (error of validationErrors; track error) {
          <div class="validation-item">{{ error }}</div>
        }
      </div>
    }

    <!-- Aggregated field-validation banner -->
    @if (hasValidationBlockers) {
      <div class="validation-banner">
        <div class="validation-banner-title">
          <i class="material-symbols-rounded">block</i>
          Required fields are missing — go back and fix these before importing
        </div>
        @for (group of validationGroups; track group.entityLabel) {
          <div class="validation-banner-group">
            <div class="group-label">{{ group.entityLabel }}</div>
            <ul>
              @for (field of group.fields; track field) {
                <li>{{ field }}</li>
              }
            </ul>
          </div>
        }
      </div>
    }

    <!-- Error with friendly guidance -->
    @if (importErrors.length > 0) {
      <div class="import-error">
        <div class="import-error-header">
          <i class="material-symbols-rounded">error</i>
          <span>Import failed</span>
        </div>

        @for (guidance of importErrorGuidance; track guidance.title) {
          <div class="import-error-guidance">
            <div class="guidance-title">{{ guidance.title }}</div>
            <div class="guidance-message">{{ guidance.message }}</div>
            @if (guidance.action) {
              <button class="guidance-action" (click)="goToStep(guidance.action.targetStep)">
                <i class="material-symbols-rounded">arrow_back</i>
                {{ guidance.action.label }}
              </button>
            }
          </div>
        }

        <details class="import-error-details">
          <summary>Technical details</summary>
          <ul class="import-error-list">
            @for (error of importErrors; track error) {
              <li>{{ error }}</li>
            }
          </ul>
        </details>
      </div>
    }

    @if (totalBills === 0 && skippedCount > 0) {
      <div class="nothing-to-import">
        <i class="material-symbols-rounded">info</i>
        <span>Every bill in this upload is already imported, so there's nothing new to import. Choose Replace on a bill to update it, or finish here.</span>
      </div>
    }

    <!-- Actions -->
    <div class="actions-bar">
      <button class="btn-secondary" (click)="goBack()" [disabled]="isImporting">
        <i class="material-symbols-rounded">arrow_back</i>
        Back
      </button>
      @if (totalBills === 0 && skippedCount > 0) {
        <button class="btn-primary" (click)="finishWithoutImport()">
          <i class="material-symbols-rounded">check</i>
          Finish
        </button>
      } @else {
        <button class="btn-primary" (click)="runImport()" [disabled]="isImporting || !importRequest || totalBills === 0 || validationErrors.length > 0 || hasValidationBlockers || importErrors.length > 0">
          @if (isImporting) {
            <span class="spinner"></span>
            Importing...
          } @else {
            <i class="material-symbols-rounded">cloud_upload</i>
            Import {{ totalBills }} {{ totalBills === 1 ? 'Bill' : 'Bills' }}
          }
        </button>
      }
    </div>
  }
</div>
